import { useMemo } from 'react'

import { DataTable } from '@/components/data-table/DataTable'
import { BLOCK_SIZE } from '@/components/data-table/virtual-blocks'
import { buildReportColumns, defaultColumnVisibility, parseSort } from '@/features/report/columns'
import { ReportFilterBar } from '@/features/report/ReportFilterBar'
import { useReportFilters } from '@/features/report/use-report-filters'
import { useCubeBlocks } from '@/hooks/use-cube-blocks'
import { useTablePreferences } from '@/hooks/use-table-preferences'
import { formatTotalsRows, type ReportSortItem, type TableReport } from '@/lib/cube-report'
import { useCubeLiveQuery } from '@/hooks/use-cube-live-query'
import { queryKeys } from '@/lib/query-keys'
import {
  buildReportFilters,
  columnValuesQuery,
  fetchAllRows,
  tableBlockKey,
  tableBlockQuery,
  tablePageQuery,
  tableTotalsQuery,
  toRows,
  toTablePage,
  totalsMeasures,
} from '@/services/cube/report'

export function TableReportView({ report }: { report: TableReport }) {
  const filters = useReportFilters(report)
  const { get, set } = filters

  const sortRaw = get('sort')

  const columns = useMemo(() => buildReportColumns(report), [report])
  const visibility = useMemo(() => defaultColumnVisibility(report), [report])
  // Küçük harf: eski sayfaların kimliğiyle aynı.
  const tableId = report.view.toLowerCase()
  const preferences = useTablePreferences(tableId, visibility)
  const sorting = useMemo(() => parseSort(sortRaw, report), [sortRaw, report])

  const sortItems: ReportSortItem[] = sorting.map((item) => ({ member: item.id, desc: item.desc }))
  // Sayfa düğmesi yok: satırlar kaydırıldıkça `BLOCK_SIZE`'lık bloklar halinde gelir (`page`/`page_size`
  // adres parametreleri yok sayılır). Süzgeç ya da sıralama değişince başa dönülür.
  const datasetKey = JSON.stringify({ filters: filters.state, sort: sortItems })
  const rows = useCubeBlocks({
    datasetKey,
    queryKey: (block) => tableBlockKey(report, filters.state, sortItems, block),
    query: (block) => tableBlockQuery(report, filters.state, sortItems, block),
    select: toTablePage,
  })

  // Toplam satırı: yalnız `meta.table.totals` varsa; aynı filtrelerle boyutsuz ayrı sorgu.
  const totalsMeasureCount = totalsMeasures(report).length
  const totals = useCubeLiveQuery({
    queryKey: queryKeys.cube(report.view, { ...tableTotalsQuery(report, filters.state) }),
    query: tableTotalsQuery(report, filters.state),
    select: toRows,
    enabled: totalsMeasureCount > 0,
  })
  const totalsRows = useMemo(
    () => (totals.data ? formatTotalsRows(report, totals.data) : undefined),
    [report, totals.data],
  )

  return (
    <>
      <ReportFilterBar report={report} filters={filters} />
      {report.note && <p className="text-muted-foreground text-xs">{report.note}</p>}
      <DataTable
        tableId={tableId}
        columns={columns}
        total={rows.total}
        virtual={{
          getRow: rows.getRow,
          loadedCount: rows.loadedCount,
          onRangeChange: rows.requestRange,
          resetKey: datasetKey,
        }}
        getRowId={(row) => String(row[report.rowKey])}
        sorting={sorting}
        onSortingChange={(updater) => {
          const next = typeof updater === 'function' ? updater(sorting) : updater
          const serialised = next
            .filter((item) => report.sortable.has(item.id))
            .map((item) => `${item.desc ? '-' : ''}${item.id}`)
            .join(',')
          set({ sort: serialised || undefined })
        }}
        isLoading={rows.isLoading}
        busy={rows.isPlaceholder}
        totalsRows={totalsRows}
        isError={rows.isError}
        error={rows.error}
        onRetry={rows.refetch}
        layout={preferences.layout}
        onLayoutChange={preferences.save}
        onLayoutReset={preferences.reset}
        fetchExportData={() =>
          fetchAllRows(tablePageQuery(report, filters.state, sortItems, 1, BLOCK_SIZE))
        }
        columnFilters={filters.state.columns}
        onColumnFiltersChange={filters.setColumns}
        filterValues={(member, search) =>
          columnValuesQuery(
            report.view,
            member,
            search,
            (state) => buildReportFilters(report, state),
            filters.state,
          )
        }
      />
    </>
  )
}
